/**
 * Crash log — a small localStorage ring buffer that captures unhandled
 * JavaScript errors, unhandled promise rejections, and React render
 * errors caught by the ErrorBoundary. Surfaced via the in-app
 * Diagnostics view so the user can read recent crashes from a
 * sideloaded tablet without dev tools, and optionally upload them to
 * the worker for triage.
 *
 * Pure-logic, side-effect-only-on-install. Tests construct a
 * `CrashLog` directly and exercise it without touching window.
 */

export interface CrashReport {
  id: string;
  /** ISO datetime — when the crash was captured. */
  timestamp: string;
  source:
    | "render"
    | "window-error"
    | "unhandled-rejection"
    | "manual";
  message: string;
  stack?: string | undefined;
  url?: string | undefined;
  userAgent?: string | undefined;
  /** React component stack — only set for `source = "render"`. */
  componentStack?: string | undefined;
  /** Free-form context (scenario id, view, session id, etc.). */
  context?: Record<string, unknown> | undefined;
}

export interface CrashLogStorage {
  read(): string | null;
  write(value: string): void;
  remove(): void;
}

const STORAGE_KEY = "radar-sim:crash-log";
const DEFAULT_CAPACITY = 50;
const SCHEMA_VERSION = 1;

interface Persisted {
  schemaVersion: number;
  reports: CrashReport[];
}

/**
 * Crash log instance. Wraps a storage adaptor (defaults to
 * `localStorage` in the browser) so tests can inject an in-memory
 * map. Bounded by `capacity`; oldest reports drop off when full.
 */
export class CrashLog {
  private readonly storage: CrashLogStorage;
  private readonly capacity: number;
  private readonly listeners = new Set<(reports: CrashReport[]) => void>();

  constructor(opts: { storage?: CrashLogStorage; capacity?: number } = {}) {
    this.storage = opts.storage ?? defaultStorage();
    this.capacity = opts.capacity ?? DEFAULT_CAPACITY;
  }

  list(): CrashReport[] {
    const persisted = this.read();
    return [...persisted.reports];
  }

  record(input: Omit<CrashReport, "id" | "timestamp">): CrashReport {
    const report: CrashReport = {
      id: randomId(),
      timestamp: new Date().toISOString(),
      ...input,
    };
    const persisted = this.read();
    persisted.reports.push(report);
    if (persisted.reports.length > this.capacity) {
      persisted.reports.splice(
        0,
        persisted.reports.length - this.capacity,
      );
    }
    this.storage.write(JSON.stringify(persisted));
    this.notify(persisted.reports);
    return report;
  }

  clear(): void {
    this.storage.remove();
    this.notify([]);
  }

  /** Subscribe to changes. Returns an unsubscribe function. */
  subscribe(handler: (reports: CrashReport[]) => void): () => void {
    this.listeners.add(handler);
    return () => {
      this.listeners.delete(handler);
    };
  }

  /** Export as a downloadable JSON blob. */
  exportJson(): string {
    return JSON.stringify(this.read(), null, 2);
  }

  private read(): Persisted {
    const raw = this.storage.read();
    if (raw === null) return { schemaVersion: SCHEMA_VERSION, reports: [] };
    try {
      const parsed = JSON.parse(raw) as Partial<Persisted>;
      if (
        parsed.schemaVersion !== SCHEMA_VERSION ||
        !Array.isArray(parsed.reports)
      ) {
        return { schemaVersion: SCHEMA_VERSION, reports: [] };
      }
      return parsed as Persisted;
    } catch {
      return { schemaVersion: SCHEMA_VERSION, reports: [] };
    }
  }

  private notify(reports: CrashReport[]): void {
    for (const handler of this.listeners) handler([...reports]);
  }
}

function defaultStorage(): CrashLogStorage {
  return {
    read: () => {
      try {
        return window.localStorage.getItem(STORAGE_KEY);
      } catch {
        return null;
      }
    },
    write: (value) => {
      try {
        window.localStorage.setItem(STORAGE_KEY, value);
      } catch {
        // localStorage may be unavailable (private mode, quota); silent.
      }
    },
    remove: () => {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        // see above
      }
    },
  };
}

function randomId(): string {
  // Deliberately not crypto.randomUUID — older Capacitor WebViews on
  // Android < 13 don't expose it. This is a diagnostics id, not a
  // security primitive.
  const r = Math.random().toString(36).slice(2, 10);
  return `c-${Date.now().toString(36)}-${r}`;
}

/**
 * Install global error listeners that funnel into the crash log.
 * Call this once at app startup, before mounting React.
 *
 * Returns an uninstall function (mainly for tests; production never
 * uninstalls).
 */
export function installGlobalCrashHandlers(
  log: CrashLog,
  context?: () => Record<string, unknown>,
): () => void {
  if (typeof window === "undefined") return () => {};

  const onError = (ev: ErrorEvent): void => {
    const message = ev.message ?? "(no message)";
    log.record({
      source: "window-error",
      message,
      stack: ev.error instanceof Error ? ev.error.stack : undefined,
      url:
        ev.filename !== undefined && ev.filename !== ""
          ? `${ev.filename}:${ev.lineno}:${ev.colno}`
          : window.location.href,
      userAgent: window.navigator.userAgent,
      context: context?.(),
    });
  };

  const onRejection = (ev: PromiseRejectionEvent): void => {
    const reason = ev.reason as unknown;
    let message = "Unhandled promise rejection";
    let stack: string | undefined;
    if (reason instanceof Error) {
      message = reason.message;
      stack = reason.stack;
    } else if (typeof reason === "string") {
      message = reason;
    } else if (reason !== null && typeof reason === "object") {
      try {
        message = JSON.stringify(reason);
      } catch {
        message = String(reason);
      }
    }
    log.record({
      source: "unhandled-rejection",
      message,
      stack,
      url: window.location.href,
      userAgent: window.navigator.userAgent,
      context: context?.(),
    });
  };

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);

  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}

/** Module-level singleton shared between ErrorBoundary, listeners, and UI. */
let singleton: CrashLog | undefined;
export function getCrashLog(): CrashLog {
  if (singleton === undefined) singleton = new CrashLog();
  return singleton;
}
